import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Field } from '../../components/ui/Field'

type TransportMeans = 'auto_personal' | 'auto_serviciu' | 'tren' | 'avion' | 'autocar' | 'alt'
type OrdinStatus = 'draft' | 'emis' | 'inchis'

interface OrdinDeplasare {
  id: number
  numar: number | null
  employeeId: number
  destinatie: string
  scop: string
  dataPlecare: string
  dataIntoarcere: string
  nrZile: number
  mijlocTransport: TransportMeans
  kmEstimati: number | null
  avansSolicitat: string
  currency: 'LEI' | 'EUR'
  status: OrdinStatus
  employee: { id: number; fullName: string }
  decont: { id: number; period: string; status: string } | null
}

interface Employee {
  id: number
  fullName: string
}

const TRANSPORT_LABEL: Record<TransportMeans, string> = {
  auto_personal: 'Auto personal',
  auto_serviciu: 'Auto serviciu',
  tren: 'Tren',
  avion: 'Avion',
  autocar: 'Autocar',
  alt: 'Altul',
}

const STATUS_CLASS: Record<OrdinStatus, string> = {
  draft: 'bg-b4l-bg text-b4l-text-muted',
  emis: 'bg-blue-100 text-blue-700',
  inchis: 'bg-green-100 text-green-700',
}

export function OrdineDeplasarePage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('hr.ordine_deplasare:write')
  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<OrdinDeplasare | null>(null)

  const query = useQuery<{ ordine: OrdinDeplasare[] }>({
    queryKey: ['ordine-deplasare'],
    queryFn: async () => (await api.get<{ ordine: OrdinDeplasare[] }>('/ordine-deplasare')).data,
  })
  const empQuery = useQuery<{ employees: Employee[] }>({
    queryKey: ['employees'],
    queryFn: async () => (await api.get<{ employees: Employee[] }>('/employees?active=true')).data,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['ordine-deplasare'] })

  const emitMutation = useMutation({
    mutationFn: async (id: number) =>
      (await api.patch(`/ordine-deplasare/${id}`, { status: 'emis' })).data,
    onSuccess: () => {
      invalidate()
      toast.success('Ordin emis (număr alocat)')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/ordine-deplasare/${id}`)).data,
    onSuccess: () => {
      invalidate()
      toast.success('Ordin șters')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const ordine = useMemo(() => query.data?.ordine ?? [], [query.data])
  const employees = useMemo(() => empQuery.data?.employees ?? [], [empQuery.data])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Ordine de deplasare</h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Ordin nou</Button>}
      </div>

      <Card>
        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : ordine.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Niciun ordin de deplasare.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-2 pr-3 font-medium">Nr.</th>
                <th className="py-2 pr-3 font-medium">Angajat</th>
                <th className="py-2 pr-3 font-medium">Destinație</th>
                <th className="py-2 pr-3 font-medium">Perioadă</th>
                <th className="py-2 pr-3 font-medium">Zile</th>
                <th className="py-2 pr-3 font-medium">Avans</th>
                <th className="py-2 pr-3 font-medium">Stare</th>
                <th className="py-2 pr-3 font-medium">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {ordine.map((o) => (
                <tr key={o.id} className="border-b border-b4l-border last:border-0">
                  <td className="py-2 pr-3 font-mono text-xs">{o.numar ?? '—'}</td>
                  <td className="py-2 pr-3 font-medium">{o.employee.fullName}</td>
                  <td className="py-2 pr-3">{o.destinatie}</td>
                  <td className="py-2 pr-3 text-xs">
                    {new Date(o.dataPlecare).toLocaleDateString('ro-RO')} –{' '}
                    {new Date(o.dataIntoarcere).toLocaleDateString('ro-RO')}
                  </td>
                  <td className="py-2 pr-3 text-center">{o.nrZile}</td>
                  <td className="py-2 pr-3 tabular-nums">
                    {Number(o.avansSolicitat).toLocaleString('ro-RO', {
                      minimumFractionDigits: 2,
                    })}{' '}
                    {o.currency}
                  </td>
                  <td className="py-2 pr-3">
                    <span className={`rounded px-2 py-0.5 text-[10px] ${STATUS_CLASS[o.status]}`}>
                      {o.status}
                    </span>
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex flex-wrap items-center gap-1">
                      {canWrite && o.status === 'draft' && (
                        <>
                          <Button
                            variant="secondary"
                            onClick={() => setEditTarget(o)}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Editează
                          </Button>
                          <Button
                            onClick={() => emitMutation.mutate(o.id)}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Emite
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm('Șterge ordinul?')) deleteMutation.mutate(o.id)
                            }}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Șterge
                          </Button>
                        </>
                      )}
                      {o.status !== 'draft' && (
                        <>
                          <Button
                            variant="secondary"
                            onClick={() => descarcaOrdin(o.id, o.numar, 'pdf')}
                            className="!px-2 !py-1 !text-xs"
                          >
                            PDF
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={() => descarcaOrdin(o.id, o.numar, 'xlsx')}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Excel
                          </Button>
                          <span className="text-xs text-b4l-text-muted">
                            {o.decont ? `decont ${o.decont.period}` : 'fără decont'}
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {createOpen && (
        <OrdinFormModal
          employees={employees}
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false)
            invalidate()
          }}
        />
      )}
      {editTarget && (
        <OrdinFormModal
          employees={employees}
          ordin={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null)
            invalidate()
          }}
        />
      )}
    </div>
  )
}

function OrdinFormModal({
  employees,
  ordin,
  onClose,
  onSaved,
}: {
  employees: Employee[]
  ordin?: OrdinDeplasare
  onClose: () => void
  onSaved: () => void
}) {
  const isCreate = !ordin
  const [employeeId, setEmployeeId] = useState(ordin ? String(ordin.employeeId) : '')
  const [destinatie, setDestinatie] = useState(ordin?.destinatie ?? '')
  const [scop, setScop] = useState(ordin?.scop ?? '')
  const [dataPlecare, setDataPlecare] = useState(ordin?.dataPlecare.slice(0, 10) ?? '')
  const [dataIntoarcere, setDataIntoarcere] = useState(ordin?.dataIntoarcere.slice(0, 10) ?? '')
  const [mijlocTransport, setMijlocTransport] = useState<TransportMeans>(
    ordin?.mijlocTransport ?? 'auto_personal',
  )
  const [kmEstimati, setKmEstimati] = useState(ordin?.kmEstimati ? String(ordin.kmEstimati) : '')
  const [avansSolicitat, setAvansSolicitat] = useState(ordin?.avansSolicitat ?? '0')
  const [currency, setCurrency] = useState<'LEI' | 'EUR'>(ordin?.currency ?? 'LEI')

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        employeeId: Number(employeeId),
        destinatie,
        scop,
        dataPlecare,
        dataIntoarcere,
        mijlocTransport,
        kmEstimati: kmEstimati.trim() === '' ? null : Number(kmEstimati),
        avansSolicitat: Number(avansSolicitat),
        currency,
      }
      if (isCreate) return api.post('/ordine-deplasare', body)
      return api.patch(`/ordine-deplasare/${ordin!.id}`, body)
    },
    onSuccess: () => {
      toast.success(isCreate ? 'Ordin creat (ciornă)' : 'Ordin salvat')
      onSaved()
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={isCreate ? 'Ordin de deplasare nou' : 'Editează ordin'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            {isCreate ? 'Creează' : 'Salvează'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-sm font-medium">Angajat</label>
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
          >
            <option value="">— alege —</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.fullName}
              </option>
            ))}
          </select>
        </div>
        <Field
          label="Destinație"
          value={destinatie}
          onChange={(e) => setDestinatie(e.target.value)}
        />
        <Field label="Scop" value={scop} onChange={(e) => setScop(e.target.value)} />
        <div className="grid grid-cols-2 gap-2">
          <Field
            label="Data plecării"
            type="date"
            value={dataPlecare}
            onChange={(e) => setDataPlecare(e.target.value)}
          />
          <Field
            label="Data întoarcerii"
            type="date"
            value={dataIntoarcere}
            onChange={(e) => setDataIntoarcere(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Mijloc transport</label>
          <select
            value={mijlocTransport}
            onChange={(e) => setMijlocTransport(e.target.value as TransportMeans)}
            className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
          >
            {Object.entries(TRANSPORT_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Field
            label="Km estimați"
            type="number"
            value={kmEstimati}
            onChange={(e) => setKmEstimati(e.target.value)}
          />
          <Field
            label="Avans solicitat"
            type="number"
            step="0.01"
            value={avansSolicitat}
            onChange={(e) => setAvansSolicitat(e.target.value)}
          />
          <div>
            <label className="mb-1 block text-sm font-medium">Monedă</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as 'LEI' | 'EUR')}
              className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
            >
              <option value="LEI">LEI</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
        </div>
      </div>
    </Modal>
  )
}

/** Descarca ordinul emis, Excel sau PDF (formular 14-5-4). */
async function descarcaOrdin(id: number, numar: number | null, format: 'xlsx' | 'pdf') {
  try {
    const res = await api.get(`/ordine-deplasare/${id}/fisier?format=${format}`, {
      responseType: 'blob',
      timeout: 120_000,
    })
    const href = URL.createObjectURL(res.data as Blob)
    const a = document.createElement('a')
    a.href = href
    a.download = `ordin-deplasare-${numar ?? id}.${format}`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(href)
  } catch (err) {
    // Raspunsul de eroare vine ca Blob (responseType), deci mesajul se citeste din el.
    const data = (err as { response?: { data?: unknown } }).response?.data
    let mesaj = 'Descărcarea n-a reușit'
    if (data instanceof Blob) {
      try {
        mesaj =
          (JSON.parse(await data.text()) as { error?: { message?: string } }).error?.message ??
          mesaj
      } catch {
        /* raspuns fara JSON */
      }
    }
    toast.error(mesaj)
  }
}

function extractError(err: unknown): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  )
}
